import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Building2, Target, Package, Users, Check, ChevronRight, ChevronLeft, Plus, X, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEIGHT_LABELS = {
  1: 'Nice to Know',
  2: 'Helpful',
  3: 'Important',
  4: 'Critical',
  5: 'Deal Breaker',
};

const STEPS = [
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'framework', label: 'Framework', icon: Target },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'team', label: 'Invite Team', icon: Users },
];

export default function AdminSetupWizard() {
  const { user, setUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1: Company
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0E4E5A');

  // Step 2: Framework
  const [frameworkChoice, setFrameworkChoice] = useState('bant');
  const [customCategories, setCustomCategories] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [customQuestions, setCustomQuestions] = useState([]);
  const [newQ, setNewQ] = useState({ question: '', category: '', weight: 3 });

  // Step 3: Products (optional)
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: '', description: '' });
  const [productMode, setProductMode] = useState('ai');
  const [scanUrl, setScanUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanProducts, setScanProducts] = useState(null);
  const [selectedScan, setSelectedScan] = useState({});
  const [applying, setApplying] = useState(false);
  const [aiAdded, setAiAdded] = useState([]);
  const [catalogueFile, setCatalogueFile] = useState(null);

  // Prefill remembered company website (from a previous wizard visit or scan)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/company/settings`);
        if (data?.company_website) {
          setScanUrl(prev => prev || data.company_website);
        }
      } catch (e) { /* non-blocking */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runCatalogueScan = async () => {
    if (!catalogueFile) {
      toast.error('Choose a catalogue file first');
      return;
    }
    setScanning(true);
    setScanProducts(null);
    try {
      const form = new FormData();
      form.append('file', catalogueFile);
      const res = await axios.post(`${API}/product-intelligence/scan-catalogue`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const prods = res.data?.scan_result?.products || [];
      if (prods.length === 0) {
        toast.info('No products found in that file — try another document or add manually.');
      }
      setScanProducts(prods);
      setSelectedScan(Object.fromEntries(prods.map((_, i) => [i, true])));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read that file — try again or add your products manually');
    } finally {
      setScanning(false);
    }
  };

  const runScan = async () => {
    if (!scanUrl.trim()) {
      toast.error('Enter your company website address first');
      return;
    }
    setScanning(true);
    setScanProducts(null);
    try {
      const res = await axios.post(`${API}/product-intelligence/scan-website`, { website_url: scanUrl });
      const prods = res.data?.scan_result?.products || [];
      if (prods.length === 0) {
        toast.info('No products found on that page — try another page or add manually.');
      }
      setScanProducts(prods);
      setSelectedScan(Object.fromEntries(prods.map((_, i) => [i, true])));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed — try again or add your products manually');
    } finally {
      setScanning(false);
    }
  };

  const applyScan = async () => {
    const chosen = (scanProducts || []).filter((_, i) => selectedScan[i]);
    if (chosen.length === 0) {
      toast.error('Select at least one product to add');
      return;
    }
    setApplying(true);
    try {
      await axios.post(`${API}/product-intelligence/apply-recommendations`, {
        products: chosen,
        questions: [],
        framework_id: null,
      });
      setAiAdded(prev => [...prev, ...chosen.map(p => p.name)]);
      setScanProducts(null);
      toast.success(`${chosen.length} product${chosen.length > 1 ? 's' : ''} added with AI discovery questions`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add products — try again');
    } finally {
      setApplying(false);
    }
  };

  // Step 4: Team invites
  const [invites, setInvites] = useState([{ email: '', role: 'sales' }]);

  const totalSteps = STEPS.length;
  const isLastStep = step === totalSteps - 1;

  const handleNext = async () => {
    if (step === 0) {
      // Save company settings
      if (!companyName.trim()) {
        toast.error('Please enter your company name');
        return;
      }
      setSaving(true);
      try {
        await axios.put(`${API}/company/settings`, {
          company_name: companyName,
          primary_color: primaryColor,
        });
      } catch (e) {
        toast.error('Failed to save company settings');
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    if (step === 1 && frameworkChoice === 'custom') {
      if (customCategories.length === 0) {
        toast.error('Add at least one category for your custom framework');
        return;
      }
    }

    if (isLastStep) {
      await handleFinish();
      return;
    }

    setStep(s => s + 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      // Remember the website typed in the wizard even if it was never scanned
      if (scanUrl.trim()) {
        try {
          await axios.put(`${API}/company/settings`, { company_website: scanUrl.trim() });
        } catch (e) { /* non-blocking */ }
      }

      // Save custom framework if selected
      if (frameworkChoice === 'custom' && customCategories.length > 0) {
        await axios.post(`${API}/frameworks`, {
          name: `${companyName} Framework`,
          description: `Custom qualification framework for ${companyName}`,
          categories: customCategories,
          questions: customQuestions,
        });
      }

      // Save products
      for (const product of products) {
        if (product.name.trim()) {
          await axios.post(`${API}/products`, {
            name: product.name,
            description: product.description,
            features: [],
            qualification_questions: [],
          });
        }
      }

      // Send invites
      for (const invite of invites) {
        if (invite.email.trim()) {
          try {
            await axios.post(`${API}/org/invite`, {
              email: invite.email,
              role: invite.role,
            });
          } catch (e) {
            // Non-critical — continue
          }
        }
      }

      // Mark setup as complete
      await axios.post(`${API}/auth/complete-setup`, {});
      setUser(prev => prev ? { ...prev, setup_completed: true } : prev);
      await refreshUser();

      toast.success('Setup complete! Welcome to PipeGuide.');
      navigate('/');
    } catch (e) {
      toast.error('Failed to complete setup');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/complete-setup`, {});
      setUser(prev => prev ? { ...prev, setup_completed: true } : prev);
      await refreshUser();
      navigate('/');
    } catch (e) {
      navigate('/');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="admin-setup-wizard">
      <div className="max-w-2xl w-full">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => {
            const StepIcon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.id} className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  isDone ? 'bg-emerald-500 text-white' :
                  isActive ? 'bg-brand-primary text-white' :
                  'bg-zinc-200 text-zinc-400'
                }`}>
                  {isDone ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${isActive ? 'text-zinc-900' : 'text-zinc-400'}`}>
                  {s.label}
                </span>
                {i < totalSteps - 1 && <div className={`w-8 sm:w-16 h-0.5 ${isDone ? 'bg-emerald-500' : 'bg-zinc-200'}`} />}
              </div>
            );
          })}
        </div>

        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="p-8">
            {/* Step 1: Company */}
            {step === 0 && (
              <div className="space-y-6" data-testid="step-company">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Set up your company</h2>
                  <p className="text-zinc-500 mt-1">This will appear in your team's PipeGuide interface.</p>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Company Name</label>
                    <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Corp" className="rounded-md" data-testid="wizard-company-name" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Brand Colour</label>
                    <div className="flex items-center gap-3">
                      <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded-md border border-zinc-200 cursor-pointer" />
                      <Input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="rounded-md w-32" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Framework */}
            {step === 1 && (
              <div className="space-y-6" data-testid="step-framework">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Choose your qualification framework</h2>
                  <p className="text-zinc-500 mt-1">This determines how PipeGuide scores your calls.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: 'bant', name: 'BANT', desc: 'Budget, Authority, Need, Timeline — classic and simple' },
                    { id: 'meddic', name: 'MEDDIC', desc: 'Metrics, Economic Buyer, Decision Criteria, Decision Process, Identify Pain, Champion' },
                    { id: 'custom', name: 'Custom', desc: 'Build your own categories and questions' },
                  ].map(fw => (
                    <div
                      key={fw.id}
                      onClick={() => setFrameworkChoice(fw.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        frameworkChoice === fw.id ? 'border-brand-primary bg-brand-muted' : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid={`fw-choice-${fw.id}`}
                    >
                      <p className="font-semibold text-zinc-900">{fw.name}</p>
                      <p className="text-xs text-zinc-500 mt-1">{fw.desc}</p>
                    </div>
                  ))}
                </div>

                {frameworkChoice === 'custom' && (
                  <div className="space-y-4 pt-4 border-t border-zinc-200">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Categories</label>
                      <div className="flex flex-wrap gap-2">
                        {customCategories.map((cat, i) => (
                          <Badge key={i} variant="outline" className="px-3 py-1">
                            {cat}
                            <button onClick={() => setCustomCategories(prev => prev.filter((_, j) => j !== i))} className="ml-2 text-zinc-400 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="e.g. Budget, Pain Points, Decision Process" className="rounded-md"
                          onKeyDown={e => { if (e.key === 'Enter' && newCat.trim()) { setCustomCategories(prev => [...prev, newCat.trim()]); setNewCat(''); }}} />
                        <Button variant="outline" onClick={() => { if (newCat.trim()) { setCustomCategories(prev => [...prev, newCat.trim()]); setNewCat(''); }}} className="rounded-md">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {customCategories.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Questions (optional — AI will also generate questions)</label>
                        {customQuestions.map((q, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-zinc-50 rounded-md text-sm">
                            <div className="flex-1">
                              <Badge variant="outline" className="text-xs">{q.category}</Badge>
                              <p className="mt-1">{q.question}</p>
                              <span className="text-xs text-zinc-500">{q.weight}/5 — {WEIGHT_LABELS[q.weight]}</span>
                            </div>
                            <button onClick={() => setCustomQuestions(prev => prev.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={newQ.category} onValueChange={v => setNewQ(prev => ({ ...prev, category: v }))}>
                            <SelectTrigger className="rounded-md"><SelectValue placeholder="Category" /></SelectTrigger>
                            <SelectContent>
                              {customCategories.map((cat, i) => <SelectItem key={i} value={cat}>{cat}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={String(newQ.weight)} onValueChange={v => setNewQ(prev => ({ ...prev, weight: parseInt(v) }))}>
                            <SelectTrigger className="rounded-md"><SelectValue placeholder="Weight" /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(WEIGHT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{k} — {v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Textarea value={newQ.question} onChange={e => setNewQ(prev => ({ ...prev, question: e.target.value }))} placeholder="Enter a question..." className="rounded-md resize-none h-16" />
                        <Button variant="outline" className="w-full rounded-md" onClick={() => {
                          if (newQ.question.trim() && newQ.category) {
                            setCustomQuestions(prev => [...prev, { ...newQ, id: Date.now().toString() }]);
                            setNewQ({ question: '', category: '', weight: 3 });
                          }
                        }}>
                          <Plus className="w-4 h-4 mr-2" /> Add Question
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Products */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-products">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Add your products or solutions</h2>
                  <p className="text-zinc-500 mt-1">Optional — helps PipeGuide suggest relevant solutions during calls. You can add more later.</p>
                </div>

                {/* Mode choice */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-mode-toggle">
                  <button
                    onClick={() => setProductMode('ai')}
                    className={`flex flex-col items-start gap-1 p-4 rounded-xl border-2 text-left transition-colors ${
                      productMode === 'ai' ? 'border-brand-primary bg-brand-muted' : 'border-zinc-200 bg-white hover:border-zinc-300'
                    }`}
                    data-testid="product-mode-ai-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 ${productMode === 'ai' ? 'text-brand-primary' : 'text-zinc-400'}`} />
                      <span className={`text-sm font-semibold ${productMode === 'ai' ? 'text-brand-primary' : 'text-zinc-700'}`}>Generate with AI</span>
                      <Badge className="bg-brand-primary text-white text-[10px] px-1.5 py-0">Recommended</Badge>
                    </div>
                    <span className="text-xs text-zinc-500">We scan your website and build your catalog for you</span>
                  </button>
                  <button
                    onClick={() => setProductMode('manual')}
                    className={`flex flex-col items-start gap-1 p-4 rounded-xl border-2 text-left transition-colors ${
                      productMode === 'manual' ? 'border-brand-primary bg-brand-muted' : 'border-zinc-200 bg-white hover:border-zinc-300'
                    }`}
                    data-testid="product-mode-manual-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className={`w-4 h-4 ${productMode === 'manual' ? 'text-brand-primary' : 'text-zinc-400'}`} />
                      <span className={`text-sm font-semibold ${productMode === 'manual' ? 'text-brand-primary' : 'text-zinc-700'}`}>Add manually</span>
                    </div>
                    <span className="text-xs text-zinc-500">Type product names and descriptions yourself</span>
                  </button>
                </div>

                {/* Already added via AI */}
                {aiAdded.length > 0 && (
                  <div className="space-y-2" data-testid="ai-added-products">
                    {aiAdded.map((name, i) => (
                      <div key={i} className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                        <Check className="w-4 h-4 text-emerald-600" />
                        <span className="flex-1 text-sm font-medium text-emerald-900">{name}</span>
                        <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">AI generated</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {productMode === 'ai' && (
                  <div className="space-y-4">
                    {!scanProducts && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-zinc-700">Your company website</label>
                        <div className="flex gap-2">
                          <Input
                            value={scanUrl}
                            onChange={e => setScanUrl(e.target.value)}
                            placeholder="www.yourcompany.com"
                            className="rounded-md flex-1"
                            disabled={scanning}
                            data-testid="wizard-scan-url-input"
                          />
                          <Button
                            onClick={runScan}
                            disabled={scanning || !scanUrl.trim()}
                            className="rounded-md bg-brand-primary hover:bg-brand-hover"
                            data-testid="wizard-scan-btn"
                          >
                            {scanning ? 'Scanning…' : (<><Sparkles className="w-4 h-4 mr-2" /> Scan with AI</>)}
                          </Button>
                        </div>
                        {scanning ? (
                          <p className="text-xs text-brand-primary animate-pulse" data-testid="wizard-scan-progress">
                            Reading your {catalogueFile && !scanUrl.trim() ? 'catalogue' : 'website'} and identifying products — this takes 30–60 seconds…
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-500">
                            The AI reads your site, builds your product catalog and writes discovery questions for each product automatically.
                          </p>
                        )}

                        {/* Catalogue upload fallback */}
                        <div className="flex items-center gap-3 pt-2">
                          <div className="flex-1 h-px bg-zinc-200" />
                          <span className="text-xs text-zinc-400">or no website? upload a price list / catalogue</span>
                          <div className="flex-1 h-px bg-zinc-200" />
                        </div>
                        <div className="flex gap-2 items-center">
                          <input
                            type="file"
                            accept=".pdf,.docx,.txt,.csv,.md"
                            onChange={e => setCatalogueFile(e.target.files?.[0] || null)}
                            disabled={scanning}
                            className="flex-1 text-sm text-zinc-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-zinc-100 file:text-zinc-700 file:text-sm file:cursor-pointer hover:file:bg-zinc-200 cursor-pointer"
                            data-testid="wizard-catalogue-file-input"
                          />
                          <Button
                            variant="outline"
                            onClick={runCatalogueScan}
                            disabled={scanning || !catalogueFile}
                            className="rounded-md"
                            data-testid="wizard-scan-catalogue-btn"
                          >
                            {scanning ? 'Scanning…' : (<><Sparkles className="w-4 h-4 mr-2" /> Scan catalogue</>)}
                          </Button>
                        </div>
                        <p className="text-xs text-zinc-400">PDF, DOCX, TXT or CSV · max 10MB · text-based files only (scanned images can&apos;t be read)</p>
                      </div>
                    )}

                    {scanProducts && scanProducts.length > 0 && (
                      <div className="space-y-3" data-testid="wizard-scan-results">
                        <p className="text-sm font-medium text-zinc-700">
                          Found {scanProducts.length} product{scanProducts.length > 1 ? 's' : ''} — untick any you don&apos;t want:
                        </p>
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                          {scanProducts.map((p, i) => (
                            <label
                              key={i}
                              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedScan[i] ? 'border-brand-accent bg-brand-muted/50' : 'border-zinc-200 bg-white opacity-60'
                              }`}
                              data-testid={`wizard-scan-product-${i}`}
                            >
                              <input
                                type="checkbox"
                                checked={!!selectedScan[i]}
                                onChange={e => setSelectedScan(prev => ({ ...prev, [i]: e.target.checked }))}
                                className="mt-1 accent-[#0E4E5A]"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-zinc-900">{p.name}</p>
                                <p className="text-xs text-zinc-500 line-clamp-2">{p.description}</p>
                                {(p.discovery_questions || []).length > 0 && (
                                  <p className="text-[11px] text-brand-primary mt-1">
                                    + {p.discovery_questions.length} discovery question{p.discovery_questions.length > 1 ? 's' : ''} included
                                  </p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={applyScan}
                            disabled={applying}
                            className="flex-1 rounded-md bg-brand-primary hover:bg-brand-hover"
                            data-testid="wizard-apply-scan-btn"
                          >
                            {applying ? 'Adding…' : (<><Check className="w-4 h-4 mr-2" /> Add {Object.values(selectedScan).filter(Boolean).length} selected</>)}
                          </Button>
                          <Button variant="outline" className="rounded-md" onClick={() => setScanProducts(null)} data-testid="wizard-rescan-btn">
                            Scan again
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {productMode === 'manual' && (
                <div className="space-y-3">
                  {products.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-zinc-50 rounded-md">
                      <Package className="w-4 h-4 text-zinc-400" />
                      <span className="flex-1 text-sm font-medium">{p.name}</span>
                      <button onClick={() => setProducts(prev => prev.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="space-y-2">
                    <Input value={newProduct.name} onChange={e => setNewProduct(prev => ({ ...prev, name: e.target.value }))} placeholder="Product or solution name" className="rounded-md" />
                    <Input value={newProduct.description} onChange={e => setNewProduct(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description (optional)" className="rounded-md" />
                    <Button variant="outline" className="w-full rounded-md" onClick={() => {
                      if (newProduct.name.trim()) {
                        setProducts(prev => [...prev, { ...newProduct }]);
                        setNewProduct({ name: '', description: '' });
                      }
                    }}>
                      <Plus className="w-4 h-4 mr-2" /> Add Product
                    </Button>
                  </div>
                </div>
                )}
              </div>
            )}

            {/* Step 4: Invite Team */}
            {step === 3 && (
              <div className="space-y-6" data-testid="step-team">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Invite your team</h2>
                  <p className="text-zinc-500 mt-1">Add your sales reps and managers. They'll get an email invite to join.</p>
                </div>
                <div className="space-y-3">
                  {invites.map((inv, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={inv.email}
                        onChange={e => setInvites(prev => prev.map((p, j) => j === i ? { ...p, email: e.target.value } : p))}
                        placeholder="colleague@company.com"
                        className="rounded-md flex-1"
                      />
                      <Select value={inv.role} onValueChange={v => setInvites(prev => prev.map((p, j) => j === i ? { ...p, role: v } : p))}>
                        <SelectTrigger className="rounded-md w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sales">Sales Rep</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                        </SelectContent>
                      </Select>
                      {invites.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => setInvites(prev => prev.filter((_, j) => j !== i))}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" className="w-full rounded-md" onClick={() => setInvites(prev => [...prev, { email: '', role: 'sales' }])}>
                    <Plus className="w-4 h-4 mr-2" /> Add Another
                  </Button>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-200">
              <div>
                {step > 0 && (
                  <Button variant="outline" onClick={() => setStep(s => s - 1)} className="rounded-md" data-testid="wizard-back-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleSkip} className="text-zinc-500" data-testid="wizard-skip-btn">
                  Skip Setup
                </Button>
                <Button onClick={handleNext} disabled={saving} className="rounded-md bg-brand-primary hover:bg-brand-hover" data-testid="wizard-next-btn">
                  {saving ? 'Saving...' : isLastStep ? (
                    <><Sparkles className="w-4 h-4 mr-2" /> Finish Setup</>
                  ) : (
                    <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
